import React from 'react';
import { decode } from 'he';
import { SocialPost, Platform, PostType } from '../../types';
import { PlatformIcon } from '../common/PlatformIcon';

interface SocialPostPreviewProps {
  post: SocialPost;
}

const isVideoUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.flv', '.m3u8'];
  return videoExtensions.some((ext) => url.toLowerCase().includes(ext));
};

export const SocialPostPreview: React.FC<SocialPostPreviewProps> = ({ post }) => {
  const platform = post.platform?.toLowerCase() as Platform;
  const postType = post.postType?.toLowerCase() as PostType;
  const profileName = post.socialProfile?.profileName || 'Profile';
  const imageUrl = post.imageUrl;
  const title = decode(post.title || '');
  const summary = decode(post.summary || '');
  const postUrl = post.postUrl;

  // Facebook Preview
  if (platform === 'facebook') {
    if (postType === 'story') {
      return (
        <div
          className="bg-black rounded-lg overflow-hidden"
          style={{ width: '240px', height: '426px' }}
        >
          {imageUrl ? (
            <div className="relative w-full h-full">
              {isVideoUrl(imageUrl) ? (
                <video
                  src={imageUrl}
                  className="w-full h-full object-cover"
                  controls={false}
                  muted
                  playsInline
                />
              ) : (
                <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
              )}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-white text-sm font-medium">{title}</p>
              </div>
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center p-8">
              <p className="text-white text-center">{title}</p>
            </div>
          )}
        </div>
      );
    }

    if (postType === 'reel' || postType === 'video') {
      return (
        <div
          className="bg-black rounded-lg overflow-hidden"
          style={{ width: '240px', height: '426px' }}
        >
          {imageUrl ? (
            <div className="relative w-full h-full">
              <video
                src={imageUrl}
                className="w-full h-full object-cover"
                controls={false}
                muted
                playsInline
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-white text-sm font-medium">{title}</p>
              </div>
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center p-8">
              <p className="text-white text-center">{title}</p>
            </div>
          )}
        </div>
      );
    }

    // Link post type - show link preview card format
    if (postType === 'link' && postUrl) {
      let hostname = '';
      try {
        hostname = new URL(postUrl).hostname;
      } catch {
        hostname = postUrl;
      }

      return (
        <div
          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm"
          style={{ width: '360px', maxWidth: '100%' }}
        >
          <div className="p-4">
            <div className="flex items-start gap-2 mb-3">
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                <PlatformIcon platform={platform} size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-gray-900 dark:text-white block mb-0.5">
                  {profileName}
                </span>
                <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  <span>Published by</span>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">TinySocial</span>
                  <span className="text-gray-400">·</span>
                  <span>3m</span>
                  <svg
                    className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-label="Public"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {imageUrl && <img src={imageUrl} alt={title} className="w-full h-32 object-cover" />}
              <div className="p-3">
                <p className="text-sm text-gray-500 dark:text-gray-400 uppercase mb-1">
                  {hostname}
                </p>
                <p className="font-semibold text-gray-900 dark:text-white mb-1">{title}</p>
                {summary && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">{summary}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Photo post type with postUrl - show photo format with "Published by"
    if (postType === 'photo' && postUrl) {
      return (
        <div
          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm"
          style={{ width: '360px', maxWidth: '100%' }}
        >
          <div className="p-4">
            <div className="flex items-start gap-2 mb-3">
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                <PlatformIcon platform={platform} size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-gray-900 dark:text-white block mb-0.5">
                  {profileName}
                </span>
                <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  <span>Published by</span>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">TinySocial</span>
                  <span className="text-gray-400">·</span>
                  <span>3m</span>
                  <svg
                    className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-label="Public"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>
            {title && (
              <p className="text-gray-900 dark:text-white mb-3 text-base leading-snug">{title}</p>
            )}
            {imageUrl && (
              <div className="rounded-lg overflow-hidden">
                {isVideoUrl(imageUrl) ? (
                  <video
                    src={imageUrl}
                    className="w-full h-auto"
                    controls={false}
                    muted
                    playsInline
                  />
                ) : (
                  <img src={imageUrl} alt={title} className="w-full h-auto" />
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Default Facebook post (photo/text) - only when no postUrl
    return (
      <div
        className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm"
        style={{ width: '360px', maxWidth: '100%' }}
      >
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
              <PlatformIcon platform={platform} size={20} />
            </div>
            <div>
              <span className="font-semibold text-gray-900 dark:text-white block">
                {profileName}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">Just now</span>
            </div>
          </div>
          {title && (
            <p className="text-gray-900 dark:text-white mb-3 whitespace-pre-wrap">{title}</p>
          )}
          {imageUrl && (
            <div className="rounded-lg overflow-hidden mb-3">
              {isVideoUrl(imageUrl) ? (
                <video
                  src={imageUrl}
                  className="w-full h-auto"
                  controls={false}
                  muted
                  playsInline
                />
              ) : (
                <img src={imageUrl} alt={title} className="w-full h-auto" />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Instagram Preview
  if (platform === 'instagram') {
    if (postType === 'story') {
      return (
        <div
          className="bg-black rounded-lg overflow-hidden"
          style={{ width: '240px', height: '426px' }}
        >
          {imageUrl ? (
            <div className="relative w-full h-full">
              {isVideoUrl(imageUrl) ? (
                <video
                  src={imageUrl}
                  className="w-full h-full object-cover"
                  controls={false}
                  muted
                  playsInline
                />
              ) : (
                <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
              )}
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500 p-0.5">
                  <div className="w-full h-full rounded-full bg-black flex items-center justify-center">
                    <PlatformIcon platform={platform} size={16} />
                  </div>
                </div>
                <span className="text-white font-semibold text-sm">{profileName}</span>
              </div>
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center p-8">
              <p className="text-white text-center">{title}</p>
            </div>
          )}
        </div>
      );
    }

    if (postType === 'reel') {
      return (
        <div
          className="bg-black rounded-lg overflow-hidden"
          style={{ width: '240px', height: '426px' }}
        >
          {imageUrl ? (
            <div className="relative w-full h-full">
              <video
                src={imageUrl}
                className="w-full h-full object-cover"
                controls={false}
                muted
                playsInline
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500 p-0.5">
                    <div className="w-full h-full rounded-full bg-black flex items-center justify-center">
                      <PlatformIcon platform={platform} size={16} />
                    </div>
                  </div>
                  <span className="text-white font-semibold text-sm">{profileName}</span>
                </div>
                {title && <p className="text-white text-sm">{title}</p>}
              </div>
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center p-8">
              <p className="text-white text-center">{title}</p>
            </div>
          )}
        </div>
      );
    }

    // Default Instagram post (photo)
    return (
      <div
        className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
        style={{ width: '360px', maxWidth: '100%' }}
      >
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500 p-0.5">
              <div className="w-full h-full rounded-full bg-white dark:bg-gray-800 flex items-center justify-center">
                <PlatformIcon platform={platform} size={16} />
              </div>
            </div>
            <span className="font-semibold text-gray-900 dark:text-white">{profileName}</span>
          </div>
          {imageUrl && (
            <div className="rounded-lg overflow-hidden mb-3" style={{ aspectRatio: '1/1' }}>
              {isVideoUrl(imageUrl) ? (
                <video
                  src={imageUrl}
                  className="w-full h-full object-cover"
                  controls={false}
                  muted
                  playsInline
                />
              ) : (
                <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
              )}
            </div>
          )}
          {title && (
            <div className="mb-2">
              <span className="font-semibold text-gray-900 dark:text-white mr-2">
                {profileName}
              </span>
              <span className="text-gray-900 dark:text-white">{title}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Twitter/X Preview
  if (platform === 'twitter' || platform === 'x') {
    return (
      <div
        className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
        style={{ width: '360px', maxWidth: '100%' }}
      >
        <div className="p-4">
          <div className="flex items-start gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-blue-400 flex items-center justify-center flex-shrink-0">
              <PlatformIcon platform={platform} size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-gray-900 dark:text-white">{profileName}</span>
                <span className="text-gray-500 dark:text-gray-400 text-sm">@username</span>
                <span className="text-gray-500 dark:text-gray-400 text-sm">·</span>
                <span className="text-gray-500 dark:text-gray-400 text-sm">Just now</span>
              </div>
              {title && (
                <p className="text-gray-900 dark:text-white mb-3 whitespace-pre-wrap">{title}</p>
              )}
              {imageUrl && (
                <div className="rounded-2xl overflow-hidden mb-2">
                  {isVideoUrl(imageUrl) ? (
                    <video
                      src={imageUrl}
                      className="w-full h-auto"
                      controls={false}
                      muted
                      playsInline
                    />
                  ) : (
                    <img src={imageUrl} alt={title} className="w-full h-auto" />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // LinkedIn Preview
  if (platform === 'linkedin') {
    if (postType === 'link' && postUrl) {
      let hostname = '';
      try {
        hostname = new URL(postUrl).hostname;
      } catch {
        hostname = postUrl;
      }

      return (
        <div
          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
          style={{ width: '360px', maxWidth: '100%' }}
        >
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
                <PlatformIcon platform={platform} size={20} />
              </div>
              <div>
                <span className="font-semibold text-gray-900 dark:text-white block">
                  {profileName}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">Just now</span>
              </div>
            </div>
            {title && <p className="text-gray-900 dark:text-white mb-3">{title}</p>}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {imageUrl && <img src={imageUrl} alt={title} className="w-full h-32 object-cover" />}
              <div className="p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">
                  {hostname}
                </p>
                <p className="font-semibold text-gray-900 dark:text-white mb-1">{title}</p>
                {summary && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">{summary}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
        style={{ width: '360px', maxWidth: '100%' }}
      >
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
              <PlatformIcon platform={platform} size={20} />
            </div>
            <div>
              <span className="font-semibold text-gray-900 dark:text-white block">
                {profileName}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">Just now</span>
            </div>
          </div>
          {title && <p className="text-gray-900 dark:text-white mb-3">{title}</p>}
          {imageUrl && (
            <div className="rounded-lg overflow-hidden mb-3">
              {isVideoUrl(imageUrl) ? (
                <video
                  src={imageUrl}
                  className="w-full h-auto"
                  controls={false}
                  muted
                  playsInline
                />
              ) : (
                <img src={imageUrl} alt={title} className="w-full h-auto" />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // YouTube Preview
  if (platform === 'youtube') {
    return (
      <div
        className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden"
        style={{ width: '280px', maxWidth: '100%' }}
      >
        {imageUrl ? (
          <div className="relative">
            <div className="relative" style={{ aspectRatio: '16/9' }}>
              {isVideoUrl(imageUrl) ? (
                <video
                  src={imageUrl}
                  className="w-full h-full object-cover"
                  controls={false}
                  muted
                  playsInline
                />
              ) : (
                <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center opacity-90">
                  <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="p-3">
              <p className="font-semibold text-gray-900 dark:text-white text-sm mb-1 line-clamp-2">
                {title}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">{profileName}</p>
            </div>
          </div>
        ) : (
          <div className="p-4">
            <p className="font-semibold text-gray-900 dark:text-white mb-2">{title}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">{profileName}</p>
          </div>
        )}
      </div>
    );
  }

  // TikTok Preview
  if (platform === 'tiktok') {
    return (
      <div
        className="bg-black rounded-lg overflow-hidden"
        style={{ width: '240px', height: '426px' }}
      >
        {imageUrl ? (
          <div className="relative w-full h-full">
            <video
              src={imageUrl}
              className="w-full h-full object-cover"
              controls={false}
              muted
              playsInline
            />
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                  <PlatformIcon platform={platform} size={16} />
                </div>
                <span className="text-white font-semibold text-sm">{profileName}</span>
              </div>
              {title && <p className="text-white text-sm">{title}</p>}
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center p-8">
            <p className="text-white text-center">{title}</p>
          </div>
        )}
      </div>
    );
  }

  // Threads Preview
  if (platform === 'threads') {
    return (
      <div
        className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
        style={{ width: '360px', maxWidth: '100%' }}
      >
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-black dark:bg-white flex items-center justify-center">
              <PlatformIcon platform={platform} size={20} />
            </div>
            <div>
              <span className="font-semibold text-gray-900 dark:text-white block">
                {profileName}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">Just now</span>
            </div>
          </div>
          {title && (
            <p className="text-gray-900 dark:text-white mb-3 whitespace-pre-wrap">{title}</p>
          )}
          {imageUrl && (
            <div className="rounded-lg overflow-hidden mb-3">
              {isVideoUrl(imageUrl) ? (
                <video
                  src={imageUrl}
                  className="w-full h-auto"
                  controls={false}
                  muted
                  playsInline
                />
              ) : (
                <img src={imageUrl} alt={title} className="w-full h-auto" />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Bluesky Preview
  if (platform === 'bluesky') {
    return (
      <div
        className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
        style={{ width: '360px', maxWidth: '100%' }}
      >
        <div className="p-4">
          <div className="flex items-start gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
              <PlatformIcon platform={platform} size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-gray-900 dark:text-white">{profileName}</span>
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  @handle.bsky.social
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-sm">·</span>
                <span className="text-gray-500 dark:text-gray-400 text-sm">Just now</span>
              </div>
              {title && (
                <p className="text-gray-900 dark:text-white mb-3 whitespace-pre-wrap">{title}</p>
              )}
              {imageUrl && (
                <div className="rounded-lg overflow-hidden mb-2">
                  {isVideoUrl(imageUrl) ? (
                    <video
                      src={imageUrl}
                      className="w-full h-auto"
                      controls={false}
                      muted
                      playsInline
                    />
                  ) : (
                    <img src={imageUrl} alt={title} className="w-full h-auto" />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default/Generic Preview
  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
      style={{ width: '360px', maxWidth: '100%' }}
    >
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-gray-500 flex items-center justify-center">
            <PlatformIcon platform={platform} size={20} />
          </div>
          <div>
            <span className="font-semibold text-gray-900 dark:text-white block">{profileName}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">Just now</span>
          </div>
        </div>
        {title && <p className="text-gray-900 dark:text-white mb-3 whitespace-pre-wrap">{title}</p>}
        {imageUrl && (
          <div className="rounded-lg overflow-hidden mb-3">
            {isVideoUrl(imageUrl) ? (
              <video src={imageUrl} className="w-full h-auto" controls={false} muted playsInline />
            ) : (
              <img src={imageUrl} alt={title} className="w-full h-auto" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
